import { lazy } from 'react';
import ProtectedRoute from '@/layouts/ProtectedRoute';
import { Route, Routes } from 'react-router-dom';
import { ROUTE_PATHS } from '@/constants/paths';
import { RouteErrorBoundary } from '@/components/Common/ErrorBoundary';
import InstructorRoster from '@/components/DailyRoster/InstructorManagement';
import StaffManager from '@/components/StaffManager';
import AddStaffManager from '@/components/StaffManager/AddStaffManager';
import StaffReport from '@/components/ReportMangers/StaffReport/StaffReport';
import StaffIndividualReport from '@/components/ReportMangers/StaffReport/StaffIndividualReport';

// Lazy-loaded route components
const AuthPage = lazy(() => import('@/components/Auth/AuthPage'));
const ResetPasswordPage = lazy(
  () => import('@/components/Auth/ResetPasswordPage')
);
const ChangePasswordPage = lazy(
  () => import('@/components/Auth/ChangePasswordPage')
);
const EditProfilePage = lazy(() => import('@/components/Auth/EditProfilePage'));

const Dashboard = lazy(() => import('@/components/Dashboard/Dashboard'));
const DailyRoster = lazy(() => import('@/components/DailyRoster'));
const AddLesson = lazy(() => import('@/components/forms/AddLesson'));
const TransportRoster = lazy(
  () => import('@/components/TransportRoster/index.tsx')
);
const LessonEntry = lazy(() => import('@/components/LessonEntry'));

const InstructorReport = lazy(
  () => import('@/components/ReportMangers/InstructorReport/InstructorReport')
);
const InstructorReportDetail = lazy(
  () =>
    import('@/components/ReportMangers/InstructorReport/InstructorReportDetail')
);
const FinancialReport = lazy(
  () => import('@/components/ReportMangers/FinancialReport/FinancialReport')
);
const GroupLesson = lazy(
  () => import('@/components/ReportMangers/GroupLessonReport/GroupLesson')
);
const TransportPaxReport = lazy(
  () =>
    import('@/components/ReportMangers/TransportPaxReport/TransportPaxReport')
);
const HotelAgentReport = lazy(
  () => import('@/components/ReportMangers/HotelReport/HotelAgentReport')
);
const HotelAgentReportDetail = lazy(
  () => import('@/components/ReportMangers/HotelReport/HotelAgentReportDetail')
);
const MountainFinancialReport = lazy(
  () =>
    import(
      '@/components/ReportMangers/MountainFinancialReport/MountainFinancialReport'
    )
);
const MountainParticipantParty = lazy(
  () =>
    import(
      '@/components/ReportMangers/MountainParticipantPartyReport/MountainParticipantParty'
    )
);

const ConfigurationPage = lazy(() => import('@/pages/ConfigurationPage'));
const AddEditConfiguration = lazy(
  () => import('@/components/Configuration/AddEditConfiguration')
);

const HotelAgentManager = lazy(() => import('@/components/HotelAgentManagers'));
const AddHotelManager = lazy(
  () => import('@/components/HotelAgentManagers/AddHotelManager')
);
const ManageCommissionRate = lazy(
  () => import('@/components/HotelAgentManagers/ManageCommissionRate')
);

const ServiceManager = lazy(() => import('@/components/ServiceManagers'));
const AddServiceManager = lazy(
  () => import('@/components/ServiceManagers/AddServiceManager')
);

const MountainManager = lazy(() => import('@/components/MountainManagers'));
const AddMountainManager = lazy(
  () => import('@/components/MountainManagers/AddMountainManager')
);

const AdminUsersManager = lazy(() => import('@/components/AdminUserManagers'));
const AdminUserPage = lazy(() => import('@/pages/AdminUserPage'));

const BookingManager = lazy(() => import('@/components/BookingManagers'));
const BookingUsers = lazy(
  () => import('@/components/BookingManagers/BookingUsers')
);
const BookingUsersList = lazy(
  () => import('@/components/BookingManagers/BookingUsersList')
);
const BroadcastManager = lazy(
  () => import('@/components/BookingManagers/BroadcastManager/index')
);
const BookingDetail = lazy(
  () => import('@/components/BookingManagers/BookingDetail')
);

const InstructorManager = lazy(() => import('@/components/InstructorManager'));
const AddInstructorManager = lazy(
  () => import('@/components/InstructorManager/AddInstructorManager')
);
const InstructorPayRate = lazy(
  () => import('@/components/InstructorManager/InstructorPayRate')
);

const TransportManager = lazy(() => import('@/components/TransportManagers'));
const AddTransportManager = lazy(
  () => import('@/components/TransportManagers/AddTransportManager')
);

const CheatSheetList = lazy(() => import('@/components/CheatSheetList'));
const DietManager = lazy(() => import('@/components/DietManagers'));
const AddDietManager = lazy(
  () => import('@/components/DietManagers/AddDietManager')
);

const PageNotFound = lazy(() => import('@/components/PageNotFound'));

const ParticipantsList = lazy(
  () => import('@/components/LessonEntry/components/ParticipantsList')
);

// Demo component for testing loading states
const LoaderDemo = lazy(() => import('@/components/UI/LoaderDemo'));

export const RoutingProvider = () => {
  return (
    <Routes>
      <Route
        path={ROUTE_PATHS.LOGIN}
        element={
          <RouteErrorBoundary>
            <AuthPage />
          </RouteErrorBoundary>
        }
      />
      <Route
        path={ROUTE_PATHS.RESET_PASSWORD}
        element={
          <RouteErrorBoundary>
            <ResetPasswordPage />
          </RouteErrorBoundary>
        }
      />
      {/* Demo route for testing loading components */}
      <Route
        path='/loader-demo'
        element={
          <RouteErrorBoundary>
            <LoaderDemo />
          </RouteErrorBoundary>
        }
      />
      <Route element={<ProtectedRoute />}>
        <Route path='/' element={<Dashboard />} />
        <Route
          path={ROUTE_PATHS.CHANGE_PASSWORD}
          element={<ChangePasswordPage />}
        />
        <Route
          path={ROUTE_PATHS.CONFIGURATION}
          element={<ConfigurationPage />}
        />
        <Route
          path={`${ROUTE_PATHS.CONFIGURATION_ADD}/:id?`}
          element={<AddEditConfiguration />}
        />
        <Route path={ROUTE_PATHS.EDIT_PROFILE} element={<EditProfilePage />} />

        <Route
          path={`${ROUTE_PATHS.HOTEL_AGENT_MANAGER_ADD}/:id?`}
          element={<AddHotelManager />}
        />

        <Route
          path={ROUTE_PATHS.HOTEL_AGENT_MANAGER_ALL}
          element={<HotelAgentManager />}
        />

        <Route
          path={ROUTE_PATHS.HOTEL_AGENT_MANAGER_COMMISSION_RATE}
          element={<ManageCommissionRate />}
        />

        <Route
          path={ROUTE_PATHS.SERVICE_MANAGER_ALL}
          element={<ServiceManager />}
        />
        <Route
          path={`${ROUTE_PATHS.SERVICE_MANAGER_ADD}/:id?`}
          element={<AddServiceManager />}
        />
        <Route
          path={ROUTE_PATHS.MOUNTAIN_MANAGER_ALL}
          element={<MountainManager />}
        />
        <Route
          path={`${ROUTE_PATHS.MOUNTAIN_MANAGER_ADD}/:id?`}
          element={<AddMountainManager />}
        />

        <Route
          path={`${ROUTE_PATHS.ADMIN_USERS_MANAGER_ADD}/:id?`}
          element={<AdminUsersManager />}
        />
        <Route
          path={ROUTE_PATHS.ADMIN_USERS_MANAGER_LIST}
          element={<AdminUserPage />}
        />
        {/* End Route */}

        <Route
          path={ROUTE_PATHS.DAILY_ROSTER}
          element={<DailyRoster />}
        ></Route>
        <Route path={ROUTE_PATHS.ADD_LESSON} element={<AddLesson />}></Route>
        <Route
          path={`${ROUTE_PATHS.INSTRUCTOR_ROSTER_LIST}/:id/:date/:type`}
          element={<InstructorRoster />}
        ></Route>

        <Route
          path={ROUTE_PATHS.TRANSPORT_ROSTER}
          element={<TransportRoster />}
        />
        <Route path={ROUTE_PATHS.LESSON_ENTRY} element={<LessonEntry />} />
        <Route
          path={`${ROUTE_PATHS.PARTICIPANTS_LIST}/:id`}
          element={<ParticipantsList />}
        />
        <Route
          path={ROUTE_PATHS.REPORT_MANAGER_INSTRUCTOR_REPORT}
          element={<InstructorReport />}
        />
        <Route
          path={`${ROUTE_PATHS.REPORT_MANAGER_INSTRUCTOR_REPORT}/:instructorId`}
          element={<InstructorReportDetail />}
        />
        <Route
          path={ROUTE_PATHS.REPORT_MANAGER_FINANCIAL_REPORT}
          element={<FinancialReport />}
        />
        <Route
          path={ROUTE_PATHS.REPORT_MANAGER_HOTEL_AGENTS_REPORT}
          element={<HotelAgentReport />}
        />
        <Route
          path={`${ROUTE_PATHS.REPORT_MANAGER_HOTEL_AGENTS_REPORT}/:hotelAgentId`}
          element={<HotelAgentReportDetail />}
        />
        <Route
          path={ROUTE_PATHS.REPORT_MANAGER_MOUNTAIN_PARTICIPANT_PARTY}
          element={<MountainParticipantParty />}
        />
        <Route
          path={ROUTE_PATHS.REPORT_MANAGER_MOUNTAIN_FINANCIAL_REPORT}
          element={<MountainFinancialReport />}
        />
        <Route
          path={ROUTE_PATHS.REPORT_MANAGER_GROUP_LESSON_REPORT}
          element={<GroupLesson />}
        />
        <Route
          path={ROUTE_PATHS.REPORT_MANAGER_TRANSPORT_PAX_REPORT}
          element={<TransportPaxReport />}
        />
        <Route
          path={ROUTE_PATHS.BOOKING_MANAGER_ALL_BOOKINGS}
          element={<BookingManager />}
        />
        <Route
          path={ROUTE_PATHS.BOOKING_MANAGER_BOOKING_USERS}
          element={<BookingUsers />}
        />
        <Route
          path={ROUTE_PATHS.BOOKING_MANAGER_BOOKING_USERS_LIST}
          element={<BookingUsersList />}
        />
        <Route
          path={ROUTE_PATHS.BOOKING_MANAGER_BROADCAST_MANAGER}
          element={<BroadcastManager />}
        />
        <Route
          path={`${ROUTE_PATHS.BOOKING_MANAGER_DETAILS}/:id?`}
          element={<BookingDetail />}
        />
        <Route
          path={ROUTE_PATHS.INSTRUCTOR_MANAGER_LIST}
          element={<InstructorManager />}
        />
        <Route
          path={`${ROUTE_PATHS.INSTRUCTOR_MANAGER_ADD}/:id?`}
          element={<AddInstructorManager />}
        />
        <Route
          path={ROUTE_PATHS.INSTRUCTOR_MANAGER_PAY_RATE}
          element={<InstructorPayRate />}
        />
        <Route
          path={ROUTE_PATHS.STAFF_MANAGER_LIST}
          element={<StaffManager />}
        />
        <Route
          path={`${ROUTE_PATHS.STAFF_MANAGER_ADD}/:id?`}
          element={<AddStaffManager />}
        />
        <Route
          path={ROUTE_PATHS.INSTRUCTOR_MANAGER_PAY_RATE}
          element={<InstructorPayRate />}
        />

        <Route
          path={ROUTE_PATHS.STAFF_MANAGER_REPORT}
          element={<StaffReport />}
        />
        <Route
          path={`${ROUTE_PATHS.STAFF_MANAGER_REPORT}/:staffId?`}
          element={<StaffIndividualReport />}
        />

        <Route
          path={ROUTE_PATHS.TRANSPORT_MANAGER_ALL}
          element={<TransportManager />}
        />
        <Route
          path={`${ROUTE_PATHS.TRANSPORT_MANAGER_ADD}/:id?`}
          element={<AddTransportManager />}
        />
        <Route
          path={ROUTE_PATHS.TRANSPORT_MANAGER_ADD}
          element={<AddTransportManager />}
        />

        <Route
          path={ROUTE_PATHS.CHEAT_SHEET_LIST}
          element={<CheatSheetList />}
        />
        <Route path={ROUTE_PATHS.DIET_MANAGER_LIST} element={<DietManager />} />
        <Route
          path={`${ROUTE_PATHS.DIET_MANAGER_ADD}/:id?`}
          element={<AddDietManager />}
        />

        <Route path='/404' element={<PageNotFound />} />
        <Route path='*' element={<PageNotFound />} />
      </Route>
      {/* Global fallback for any unmatched route when not authenticated */}
      <Route path='*' element={<PageNotFound />} />
    </Routes>
  );
};
